// File: frontend/src/pages/PrivacyPolicy.tsx
import React from 'react';
import { Box, Container, Paper, Typography, Link, Divider } from '@mui/material';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SEOHead from '../components/SEOHead';
import { LegalPageHeader, SectionHeading, NoticeBox } from '../components/LegalPageLayout';
import { cyber } from '../theme';

const PrivacyPolicy = () => {
    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
            <SEOHead
                title="Privacy Policy - TI Mindmap HUB"
                description="Privacy Policy for TI Mindmap HUB. Learn how we collect, use, and protect your data in this independent research project."
                canonical="/privacy"
            />
            <Container maxWidth="md">
                <Paper sx={{ p: { xs: 3, md: 5 }, border: `1px solid ${cyber.border}` }}>
                    <LegalPageHeader
                        icon={<PrivacyTipIcon sx={{ fontSize: 28 }} />}
                        title="Privacy Policy"
                        subtitle="How we collect, use, and protect your data"
                        lastUpdated="January 2026"
                    />

                    <Typography variant="body1" paragraph>
                        This Privacy Policy describes how TI Mindmap HUB, an independent research project maintained by an individual researcher ("we," "us," or "our"), collects, uses, and shares information in connection with your use of the TI Mindmap application (the "Service"). This Service is a non-commercial research project and proof of concept.
                    </Typography>

                    {/* Independence Notice */}
                    <NoticeBox>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Note:</strong> TI Mindmap HUB is an independent research project. It is not affiliated with, endorsed by, or representative of any organization or employer.
                        </Typography>
                    </NoticeBox>

                    <SectionHeading>1. Information We Collect</SectionHeading>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li><Typography><b>Account Information:</b> When you authenticate using Azure AD B2C, we receive your Name and Email Address to identify you as a user. A unique, anonymous identifier (oid) is assigned to your account.</Typography></li>
                        <li><Typography><b>User Submissions:</b> If you submit an article URL for processing, we store the URL you provided along with your unique user identifier.</Typography></li>
                        <li><Typography><b>Usage Data:</b> We may collect anonymous data about your interaction with the service, such as which features are used, for the sole purpose of improving the application. IP addresses may be logged by our hosting provider (Microsoft Azure) for security and diagnostic purposes.</Typography></li>
                    </Box>

                    <SectionHeading>2. How We Use Your Information</SectionHeading>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li><Typography><b>To Provide and Maintain the Service:</b> To manage your account, provide access to features, and process your article submissions.</Typography></li>
                        <li><Typography><b>To Improve the Service:</b> To understand how the Service is used and to identify areas for improvement.</Typography></li>
                        <li><Typography><b>To Communicate With You:</b> To respond to your inquiries or provide information about the Service.</Typography></li>
                        <li><Typography><b>For Research Purposes:</b> Aggregated, anonymized usage patterns may be used to inform research on AI applications in threat intelligence.</Typography></li>
                    </Box>

                    <SectionHeading>3. AI Processing and Your Data</SectionHeading>
                    <Typography paragraph>
                        The Service uses Generative AI (Large Language Models) to process threat intelligence content. Important clarifications:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li><Typography><b>No Training on User Data:</b> Your submitted URLs and personal data are not used to train or fine-tune any AI models.</Typography></li>
                        <li><Typography><b>Processing Only:</b> AI models are used solely to analyze and transform publicly available threat intelligence content.</Typography></li>
                        <li><Typography><b>Third-Party AI Services:</b> We may use third-party AI APIs (such as OpenAI or Anthropic) to process content. These services have their own privacy policies and data handling practices.</Typography></li>
                    </Box>

                    <SectionHeading>4. Data Storage and Security</SectionHeading>
                    <Typography paragraph>Your personal data is stored on secure servers provided by Microsoft Azure. The Submissions and Users data is stored in Azure Cosmos DB. We take reasonable measures to protect your information, but no security system is impenetrable.</Typography>

                    <SectionHeading>5. Data Retention</SectionHeading>
                    <Typography paragraph>
                        We retain your data for the following periods:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li><Typography><b>Account Information:</b> Retained while your account is active and for up to 12 months after account deletion.</Typography></li>
                        <li><Typography><b>User Submissions:</b> Retained indefinitely as part of the intelligence repository unless you request deletion.</Typography></li>
                        <li><Typography><b>Usage Logs:</b> Retained for up to 90 days for diagnostic purposes.</Typography></li>
                    </Box>
                    <Typography paragraph sx={{ mt: 2 }}>
                        You may request deletion of your data at any time by contacting us.
                    </Typography>

                    <SectionHeading>6. Your Data Rights (GDPR)</SectionHeading>
                    <Typography paragraph>If you are a resident of the European Economic Area (EEA), you have certain data protection rights, including the right to access, update, delete, or port your information. To exercise any of these rights, please contact us at <Link href="mailto:info@ti-mindmap-hub.com">info@ti-mindmap-hub.com</Link>. We will respond to your request within 30 days.</Typography>

                    <SectionHeading>7. Cookies</SectionHeading>
                    <Typography paragraph>Our service uses cookies for essential functions like authentication (keeping you logged in). We may also use cookies for analytics to help us understand how the service is used. You will be asked for consent to use non-essential cookies via a banner when you first visit the site.</Typography>

                    <SectionHeading>8. Third-Party Services</SectionHeading>
                    <Typography paragraph>
                        The Service integrates with the following third-party services, each with their own privacy policies:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li><Typography>Microsoft Azure (hosting, authentication, database)</Typography></li>
                        <li><Typography>Google Analytics (usage analytics)</Typography></li>
                        <li><Typography>AI model providers for content processing</Typography></li>
                    </Box>

                    <SectionHeading>9. Changes to This Privacy Policy</SectionHeading>
                    <Typography paragraph>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.</Typography>

                    <Divider sx={{ my: 3 }} />

                    <SectionHeading>Contact Us</SectionHeading>
                    <Typography paragraph>
                        If you have any questions about this Privacy Policy or our data practices, please contact us at: <Link href="mailto:info@ti-mindmap-hub.com">info@ti-mindmap-hub.com</Link>
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;
